import { Equipment, EquipmentCostHistory, EquipmentModelType } from '__generated__/graphql';
import { IEquipmentDevice } from 'hooks/models/equipment/useEquipmentDevices';
import { t } from 'i18next';
import { first, isNil, orderBy } from 'lodash';
import { DateTime } from 'luxon';
import { TRpcError } from 'services/rpc/types';
import IEquipment from 'types/Equipment';
import ITimeEntry from 'types/TimeEntry';
import ITimeRange from 'types/TimeRange';
import { Nullable } from 'types/util/Nullable';
import { Optional } from 'types/util/Optional';
import { equipmentModelTypeStringMap } from './constants/equipmentConstants';
import { dateTimeFromUtcISO } from './dateUtils';

export interface IEquipmentDisplayData {
  equipmentName: string;
  model?: Optional<{
    unknown?: Optional<boolean>;
    title?: Optional<string>;
    year?: Optional<number>;
    make?: Optional<{
      unknown?: Optional<boolean>;
      title?: Optional<string>;
    }>;
  }>;
}

export function getDeviceCoupleErrorMessage(error: Nullable<TRpcError>): string {
  if (!isNil(error) && error.message?.toLowerCase().includes('start date conflict')) {
    return t(
      'The installation date conflicts with a previous coupling of this device. Please choose a different date and try again.'
    );
  }
  return t('There was an error coupling the device.');
}

export function makeModelString(equipment: IEquipmentDisplayData, emptyText: string = '---'): string {
  if (equipment.model && equipment.model.make && !equipment.model.unknown && !equipment.model.make?.unknown) {
    return equipment.model.make.title + ' ' + equipment.model.title;
  }
  return emptyText;
}

export function makeModelYearString(equipment: IEquipmentDisplayData): string {
  if (equipment.model && equipment.model.make && !equipment.model.unknown && !equipment.model.make?.unknown) {
    if (equipment.model?.year) {
      return equipment.model.make.title + ' ' + equipment.model.title + ' ' + equipment.model.year.toString();
    } else {
      return makeModelString(equipment);
    }
  }
  return '---';
}

// image
export function equipmentImageUrl(equipment: IEquipment | Equipment): string | undefined {
  if (equipment.imageUrl) {
    return equipment.imageUrl;
  } else if (equipment.model && equipment.model.imageUrl) {
    return equipment.model.imageUrl;
  } else if (equipment.model && equipment.model.category && equipment.model.category.imageUrl) {
    return equipment.model.category.imageUrl;
  }

  return undefined;
}

export function getCostHistoryForRange(
  costHistories: EquipmentCostHistory[],
  timeRange: ITimeRange<DateTime>
): EquipmentCostHistory | undefined {
  const sortedCostHistories = orderBy(
    costHistories.filter((costHistory) => isNil(costHistory.deletedOn)),
    'changeDate',
    'desc'
  );

  return first(
    sortedCostHistories.filter((costHistory) => dateTimeFromUtcISO(costHistory.changeDate) <= timeRange.endTime)
  );
}

export function getFutureEquipmentCostHistory<T extends Pick<EquipmentCostHistory, 'deletedOn' | 'changeDate'>>(
  costHistories: T[]
): T | undefined {
  return first(
    costHistories.filter((costHistory) => {
      return (
        costHistory?.deletedOn === null &&
        costHistory?.changeDate &&
        DateTime.fromISO(costHistory.changeDate).startOf('day') > DateTime.local().startOf('day')
      );
    })
  );
}

export function getCurrentEquipmentCostHistory<
  T extends Pick<EquipmentCostHistory, 'deletedOn' | 'changeDate' | 'updatedOn'>,
>(costHistories: T[]): T | undefined {
  return first(
    orderBy(
      costHistories.filter((costHistory) => {
        return (
          costHistory?.deletedOn === null &&
          costHistory?.changeDate &&
          DateTime.fromISO(costHistory.changeDate).startOf('day') <= DateTime.local().startOf('day')
        );
      }),
      ['changeDate', 'createdOn'],
      ['desc', 'desc']
    )
  );
}

export function parseEquipmentModelType(candidate: Nullable<string>): Nullable<EquipmentModelType> {
  if (candidate === null) {
    return null;
  }

  return equipmentModelTypeStringMap[candidate] ?? null;
}

export function StringToEquipmentModelType(value: string): EquipmentModelType {
  switch (value) {
    case EquipmentModelType.Attachment:
    case EquipmentModelType.Equipment:
    case EquipmentModelType.Tool:
      return value;
    default:
      throw new Error(`Invalid EquipmentModelType: ${value}`);
  }
}

export function getEquipmentWithAddOnsDisplayFromTimeEntry(timeEntry: ITimeEntry, hasMultiEquipmentTracking: boolean) {
  const equipment = timeEntry.equipment?.equipmentName || null;
  const addOns =
    hasMultiEquipmentTracking && timeEntry.addOns?.length
      ? timeEntry.addOns.map((addOn) => addOn.equipment.equipmentName)
      : [];
  const addOnCount = addOns.length;
  const firstAddOn = first(addOns);

  let equipmentText: string;

  if (equipment && addOnCount === 1) {
    equipmentText = `${equipment}, ${firstAddOn}`;
  } else if (!equipment && addOnCount === 2) {
    equipmentText = addOns.join(', ');
  } else if (equipment && addOnCount > 1) {
    equipmentText = t(`${equipment} + ${addOnCount} Add-Ons`);
  } else if (!equipment && addOnCount > 2) {
    equipmentText = t(`${firstAddOn} + ${addOnCount - 1} more`);
  } else {
    equipmentText = equipment || firstAddOn || t('No Equipment');
  }

  return equipmentText;
}

export function getLimitingMaxDate(
  currentDevice: IEquipmentDevice | null,
  selectedDevice: IEquipmentDevice | null
): DateTime {
  const maxDate = DateTime.utc().minus({ days: 7 }).endOf('day');

  const dates: DateTime[] = [maxDate];

  if (currentDevice?.id && currentDevice.id !== selectedDevice?.id) {
    const currentDate = currentDevice.current_coupled_date || currentDevice.last_uncoupled_date;
    if (currentDate) {
      dates.push(DateTime.fromISO(currentDate));
    }

    const selectedDate = selectedDevice?.current_coupled_date || selectedDevice?.last_uncoupled_date;
    if (selectedDate) {
      dates.push(DateTime.fromISO(selectedDate));
    }
  }

  return dates.reduce((latest, d) => (d > latest ? d : latest), maxDate);
}
